import { Set } from 'immutable';
import { SortOrder } from '@opengov/data-viz';
import { OG_META_DATA_KEY } from '@opengov/reporting-types';
import isBlank from '@sedan-utils/is-blank';
import Big from 'big.js';
import { format } from 'd3-format';
import { pie } from 'd3-shape';
import _ from 'lodash';

const bigNumberMaxValue = Big(Number.MAX_VALUE);

export const ensureNumberSafety = (num: string | number): void => {
  if (isBlank(num)) {
    return;
  }
  if (Big(num).gte(bigNumberMaxValue)) {
    throw new Error('Numbers are larger than Number.MAX_SAFE_INTEGER');
  }
};

export const getDefaultAggregateTotal = (data): number => data[data.length - 1][OG_META_DATA_KEY].aggregateTotal;

export const buildBreakdownDataStructure = (data: any[], categories: any[], units: string = 'scalar') => {
  const organizeDataByCategory = (columnData) => {
    const columnDataCategories = Object.keys(columnData.data);
    return columnDataCategories.map((category: string) => ({
      data: columnData,
      name: category,
      value: formatMetric(columnData.data[category], units),
      numeric: columnData.data[category]
    }));
  };

  const allColumns = data.map((d, index) => ({ id: index, label: d.name }));
  return { data: data.map(organizeDataByCategory), domainKeys: categories, allColumns };
};

export const buildCategory = (category, value, total) => {
  return {
    name: category,
    value: value,
    numeric: null,
    data: {
      value,
      amount: value,
      totals: total
    }
  };
};

export const buildBreakdownDataStructureV2 = (data, categories) => {
  const valueAssessor = (d, category) => d.data[category];
  const totalAssessor = (d) => parseFloat(d.totals);

  return categories.reduce((accumulator: any, category) => {
    const value = valueAssessor ? _.sum(_.compact(data.map((d) => valueAssessor(d, category)))) : 0;
    const total = totalAssessor ? _.sum(_.compact(data.map(totalAssessor))) : 0;
    return [...accumulator, buildCategory(category, value, total)];
  }, []);
};

export const buildPieSeries = (series) =>
  series.map((data) => {
    // build pie series with absolute numeric values
    // negative value is treated as zero in the d3-shape library
    return pie()
      .value((d: any) => Math.abs(d.numeric))
      .sort(sortPieSlices)(data);
  });

const unwantedCategoryKeys = ['data', 'id', 'type', 'name', 'value', 'totals'];

export const getCategories = (data): Set<string> =>
  data.reduce((categories, d) => {
    if (d.data) {
      return categories.merge(Object.keys(d.data));
    }

    // these are not 'data' keys ... ugh
    const keys = Object.keys(d).filter((k) => !unwantedCategoryKeys.includes(k));

    return categories.merge(keys);
  }, Set<string>());

export const sortPieSlicesBySortOrder = (sortOrder: SortOrder) => (a, b) => {
  // always put the Other slice last
  if (a.name === 'Other' || b.name === 'Other') {
    return 0;
  }

  switch (sortOrder) {
    case SortOrder.alphaNumericAscending:
    case SortOrder.alphaNumericDescending:
      if (a.name > b.name) {
        return 0;
      }
      if (a.name < b.name) {
        return -1;
      }
      break;

    case SortOrder.numericAscending:
      if (Number(a.value) < Number(b.value)) {
        return 0;
      }
      if (Number(a.value) > Number(b.value)) {
        return -1;
      }
      break;

    case SortOrder.numericDescending:
      if (Number(a.value) > Number(b.value)) {
        return 0;
      }
      if (Number(a.value) < Number(b.value)) {
        return -1;
      }
      break;
    default:
      throw new Error('undefined sortOrder');
  }

  return 0;
};

export const sortPieSlices = (a, b) => {
  // always put the Other slice last
  if (a.name === 'Other' || b.name === 'Other') {
    return 0;
  }
  if (Number(a.value) < Number(b.value)) {
    return 1;
  }
  if (Number(a.value) > Number(b.value)) {
    return -1;
  }
  return 0;
};

export const calculatePercent = (value, totals): string => {
  if (totals === 0) {
    return (100).toFixed(2);
  }
  return Big(value).div(totals).abs().times(100).toFixed(2);
};

export const toMoney = (num: string): string => {
  const numWithCommas = parseFloat(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return numWithCommas.charAt(0) === '-' ? `-$${numWithCommas.slice(1)}` : `$${numWithCommas}`;
};

export const localizeNumber = (num: string | number): string => {
  return typeof num === 'string' ? parseFloat(num).toLocaleString() : num.toLocaleString();
};

export const toPercent = (num: string | number, precision: number = 0): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: precision
  });
  return formatter.format(typeof num === 'string' ? parseFloat(num) : num);
};

export const formatMetric = (val: string, units: string): string => {
  if (!val) {
    return '';
  }
  if (val === 'N/A') {
    return val;
  }
  switch (units) {
    case 'percent':
    case 'percentOfTotal':
      return toPercent(val, 2);
    case 'scalar':
    case 'FTEs':
      return localizeNumber(val);
    default:
      return toMoney(val);
  }
};

export const convertTypeToText = (ledger: string): string => {
  if (ledger) {
    switch (ledger) {
      case 'liabilities':
        return 'Liabilities & Equities';
      case 'assets_less_liabilities_and_equities':
        return 'Assets Less Liabilities & Equities';
      case 'revenues_less_expenses':
        return 'Revenues Less Expenses';
      case 'fteCount':
        return 'FTE';
      default:
        return `${ledger.charAt(0).toUpperCase() + ledger.substring(1)}`;
    }
  } else {
    return '';
  }
};

// Formats number for better readability.
export const numericFormatter = (num: number): string => {
  if (num < 1000 && num > -1000) return `${num}`;
  return format('.2s')(num)
    .replace('P', 'Q')
    .replace('G', 'B')
    .replace('k', 'K')
    .replace(/(\.0+)(\w)/, '$2');
};

export const elementSome = (value) => (v) => Number(v.id) === Number(value.id) && value.category === v.category;

export const getLastIdInFlatData = (flatData) => {
  return flatData[flatData.length - 1].id.toString();
};

export const getFirstIdInFlatData = (flatData) => {
  return flatData[0].id.toString();
};

export const formatShownMetricsCount = (shownMetricsCount: number, totalMetricsCount: number): string => {
  return `Showing top ${shownMetricsCount} of ${totalMetricsCount} metrics`;
};

export const getCurrentValue = (data): number => {
  const currentData = data[data.length - 1]; //.data.data;
  if (!currentData) {
    return null;
  }

  // check new data format first
  if (currentData[OG_META_DATA_KEY]) {
    return currentData[OG_META_DATA_KEY].aggregateTotal;
  }

  return Number(currentData.value || currentData.totals);
};

export const EMPTY_TABLE_MESSAGE = 'We’re having trouble loading the report';
export const SUMMARY_TABLE_DATA_UPDATE_TEXT = 'Data is not available at this time...';
